/**
 * Máscara dd/mm/aaaa: hasta 8 dígitos en orden con separadores /.
 * - Inserta / al escribir "/" tras día o mes.
 * - Si `prevDigitCount` es el que devuelve el campo (ver DatePicker), inserta / al pasar a 2 o 4 dígitos (evita bloquear el retroceso).
 */
export function maskInputDdMmYyyy(raw, prevDigitCount = undefined) {
  const s = String(raw ?? '')
  const digits = s.replace(/\D/g, '').slice(0, 8)
  const len = digits.length
  if (len === 0) return ''

  let out
  if (len <= 2) out = digits
  else if (len <= 4) out = `${digits.slice(0, 2)}/${digits.slice(2)}`
  else out = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`

  const endsWithSlash = s.replace(/\s+$/, '').endsWith('/')
  const slashAfterDay =
    len === 2 &&
    !out.endsWith('/') &&
    (endsWithSlash || prevDigitCount === 1)
  const slashAfterMonth =
    len === 4 &&
    !out.endsWith('/') &&
    (endsWithSlash || prevDigitCount === 3)

  if (slashAfterDay || slashAfterMonth) {
    out = `${out}/`
  }
  return out.slice(0, 10)
}

/** Convierte DD/MM/AAAA a YYYY-MM-DD si la fecha es calendario-válida; si no, null. */
export function parseDdMmYyyyToIso(s) {
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(String(s ?? '').trim())
  if (!m) return null
  const d = Number(m[1])
  const mo = Number(m[2])
  const y = Number(m[3])
  if (!Number.isFinite(d) || !Number.isFinite(mo) || !Number.isFinite(y)) return null
  const date = new Date(y, mo - 1, d)
  if (date.getFullYear() !== y || date.getMonth() !== mo - 1 || date.getDate() !== d) return null
  return `${y}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`
}

/** Convierte YYYY-MM-DD a DD/MM/AAAA para mostrar. */
export function formatIsoToDdMmYyyy(iso) {
  const t = String(iso ?? '').trim()
  if (!t) return ''
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(t)
  if (!m) return ''
  return `${m[3]}/${m[2]}/${m[1]}`
}

/** Primeros 10 caracteres YYYY-MM-DD desde API u otra cadena ISO. */
export function normalizeIsoDateString(value) {
  const t = String(value ?? '').trim()
  if (!t) return ''
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(t)
  return m ? m[1] : ''
}

export function todayIsoLocal() {
  const d = new Date()
  const y = d.getFullYear()
  const mo = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${mo}-${day}`
}

/**
 * Convierte fecha/hora de la API (ISO u objeto Date) a YYYY-MM-DD en calendario local.
 * Si la API manda solo fecha `YYYY-MM-DD` o ISO con medianoche UTC (`…T00:00:00Z`),
 * `new Date(valor)` en JS interpreta UTC y en zonas como AR puede retroceder un día;
 * en ese caso se usa la parte fecha como día civil local.
 */
export function apiDateTimeToIsoLocal(value) {
  if (value == null || value === '') return ''
  if (value instanceof Date) {
    const d = value
    if (Number.isNaN(d.getTime())) return ''
    const y = d.getFullYear()
    const mo = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    return `${y}-${mo}-${day}`
  }
  const s = String(value).trim()
  const m = /^(\d{4})-(\d{2})-(\d{2})(?=$|[Tt\s])/.exec(s)
  if (m) {
    const y = Number(m[1])
    const mo = Number(m[2])
    const d = Number(m[3])
    const local = new Date(y, mo - 1, d)
    if (
      local.getFullYear() === y &&
      local.getMonth() === mo - 1 &&
      local.getDate() === d
    ) {
      return `${m[1]}-${m[2]}-${m[3]}`
    }
    return ''
  }
  const parsed = new Date(s)
  if (Number.isNaN(parsed.getTime())) return ''
  const y = parsed.getFullYear()
  const mo = String(parsed.getMonth() + 1).padStart(2, '0')
  const day = String(parsed.getDate()).padStart(2, '0')
  return `${y}-${mo}-${day}`
}

/** YYYY-MM-DD local: hoy a medianoche + `deltaDays` (negativo = hacia el pasado). */
export function addDaysToTodayIsoLocal(deltaDays) {
  const d = new Date()
  d.setDate(d.getDate() + Number(deltaDays))
  const y = d.getFullYear()
  const mo = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${mo}-${day}`
}

/**
 * YYYY-MM-DD local + `deltaDays` partiendo de un ISO arbitrario (negativo = hacia el pasado).
 * Devuelve cadena vacía si el ISO de entrada no es válido.
 */
export function addDaysToIsoLocal(iso, deltaDays) {
  const base = normalizeIsoDateString(iso)
  if (!base) return ''
  const [y, mo, day] = base.split('-').map(Number)
  const d = new Date(y, mo - 1, day)
  d.setDate(d.getDate() + Number(deltaDays))
  const yy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${yy}-${mm}-${dd}`
}

/**
 * Parsea YYYY-MM-DD a `{ birth, today }` (mediodía local para evitar desfases TZ),
 * o `null` si el ISO es inválido.
 */
function parseBirthAndToday(iso) {
  const s = normalizeIsoDateString(iso)
  if (!s) return null
  const [y, m, d] = s.split('-').map(Number)
  const birth = new Date(y, m - 1, d, 12, 0, 0)
  if (Number.isNaN(birth.getTime())) return null
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12, 0, 0)
  return { birth, today }
}

/** Edad en años cumplidos a partir de YYYY-MM-DD. */
export function computeAgeYearsFromIso(iso) {
  const t = parseBirthAndToday(iso)
  if (!t) return null
  let years = t.today.getFullYear() - t.birth.getFullYear()
  const monthDiff = t.today.getMonth() - t.birth.getMonth()
  if (monthDiff < 0 || (monthDiff === 0 && t.today.getDate() < t.birth.getDate())) {
    years -= 1
  }
  return years < 0 ? null : years
}

/** Edad en meses cumplidos a partir de YYYY-MM-DD. */
export function computeAgeMonthsFromIso(iso) {
  const t = parseBirthAndToday(iso)
  if (!t) return null
  let months =
    (t.today.getFullYear() - t.birth.getFullYear()) * 12 +
    (t.today.getMonth() - t.birth.getMonth())
  if (t.today.getDate() < t.birth.getDate()) months -= 1
  return months < 0 ? null : months
}

/** Edad en días cumplidos a partir de YYYY-MM-DD. */
export function computeAgeDaysFromIso(iso) {
  const t = parseBirthAndToday(iso)
  if (!t) return null
  const ms = t.today.getTime() - t.birth.getTime()
  if (ms < 0) return null
  return Math.floor(ms / (1000 * 60 * 60 * 24))
}
